import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Users, User } from 'lucide-react';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { Skeleton } from '@/components/ui/skeleton';

export const ControlPanel = () => {
  const { settings, isLoading, updateSetting } = useAdminSettings();

  if (isLoading) {
    return (
      <Card className="bg-black border-white/10">
        <CardHeader className="border-b border-white/5">
          <CardTitle className="flex items-center gap-2 text-white">
            <Settings className="h-5 w-5 text-white" />
            Painel de Controle
          </CardTitle>
          <CardDescription className="text-white/70">Gerenciar configurações globais da plataforma</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-10 w-full bg-white/5" />
          <Skeleton className="h-10 w-full bg-white/5" />
          <Skeleton className="h-10 w-full bg-white/5" />
        </CardContent>
      </Card>
    );
  }

  if (!settings) return null;

  const handleTestProfilesToggle = (checked: boolean) => {
    updateSetting.mutate({
      key: 'admin_show_test_profiles',
      value: checked,
    });
  };

  const handleRoleChange = (value: string) => {
    let roles: ('sponsor' | 'sponsored')[] | string;
    
    if (value === 'profile_based') {
      roles = 'profile_based';
    } else if (value === 'both') {
      roles = ['sponsor', 'sponsored'];
    } else {
      roles = [value as 'sponsor' | 'sponsored'];
    }
    
    updateSetting.mutate({
      key: 'admin_visible_roles',
      value: roles,
    });
  };

  const handleGenderChange = (value: string) => {
    let genders: ('male' | 'female' | 'other')[] | string;
    
    if (value === 'orientation_based') {
      genders = 'orientation_based';
    } else if (value === 'all') {
      genders = ['male', 'female', 'other'];
    } else {
      genders = [value as 'male' | 'female' | 'other'];
    }
    
    updateSetting.mutate({
      key: 'admin_visible_genders',
      value: genders,
    });
  };

  // Derive current values from settings
  const currentRole = 
    settings.admin_visible_roles === 'profile_based' ? 'profile_based' :
    Array.isArray(settings.admin_visible_roles) && settings.admin_visible_roles.length === 2 ? 'both' : 
    Array.isArray(settings.admin_visible_roles) ? settings.admin_visible_roles[0] : 'profile_based';
  
  const currentGender = 
    settings.admin_visible_genders === 'orientation_based' ? 'orientation_based' :
    Array.isArray(settings.admin_visible_genders) && settings.admin_visible_genders.length === 3 ? 'all' : 
    Array.isArray(settings.admin_visible_genders) ? settings.admin_visible_genders[0] : 'orientation_based';

  return (
    <Card className="bg-black border-white/10">
      <CardHeader className="border-b border-white/5">
        <CardTitle className="flex items-center gap-2 text-white">
          <Settings className="h-5 w-5 text-white" />
          Painel de Controle
        </CardTitle>
        <CardDescription className="text-white/70">
          Gerenciar visualização e comportamento da plataforma
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Test Profiles Toggle */}
        <div className="flex items-center justify-between space-x-4 rounded-lg border border-white/10 p-4 bg-zinc-900/50">
          <div className="flex-1 space-y-1">
            <Label htmlFor="test-profiles" className="text-base font-medium text-white">
              Exibir perfis de teste
            </Label>
            <p className="text-sm text-white/60">
              Mostrar ou ocultar perfis que começam com "test"
            </p>
          </div>
          <Switch
            id="test-profiles"
            checked={settings.admin_show_test_profiles}
            onCheckedChange={handleTestProfilesToggle}
            disabled={updateSetting.isPending}
          />
        </div>

        {/* Role Filter */}
        <div className="space-y-3 rounded-lg border border-white/10 p-4 bg-zinc-900/50">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-white/70" />
            <Label htmlFor="role-filter" className="text-base font-medium text-white">
              Visualizar usuários
            </Label>
          </div>
          <Select
            value={currentRole}
            onValueChange={handleRoleChange}
            disabled={updateSetting.isPending}
          >
            <SelectTrigger id="role-filter" className="bg-zinc-900 border-white/10 text-white">
              <SelectValue placeholder="Selecione os tipos de usuário" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="profile_based" className="text-white focus:bg-white/10 focus:text-white">
                🎯 Baseado no meu perfil
              </SelectItem>
              <SelectItem value="both" className="text-white focus:bg-white/10 focus:text-white">
                Sponsors e Sponsored
              </SelectItem>
              <SelectItem value="sponsor" className="text-white focus:bg-white/10 focus:text-white">
                Apenas Sponsors
              </SelectItem>
              <SelectItem value="sponsored" className="text-white focus:bg-white/10 focus:text-white">
                Apenas Sponsored
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-white/60">
            Filtrar quais tipos de perfil aparecem no painel
          </p>
        </div>

        {/* Gender Filter */}
        <div className="space-y-3 rounded-lg border border-white/10 p-4 bg-zinc-900/50">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-white/70" />
            <Label htmlFor="gender-filter" className="text-base font-medium text-white">
              Exibir gêneros
            </Label>
          </div>
          <Select
            value={currentGender}
            onValueChange={handleGenderChange}
            disabled={updateSetting.isPending}
          >
            <SelectTrigger id="gender-filter" className="bg-zinc-900 border-white/10 text-white">
              <SelectValue placeholder="Selecione os gêneros" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="orientation_based" className="text-white focus:bg-white/10 focus:text-white">
                🎯 Baseado na minha orientação
              </SelectItem>
              <SelectItem value="all" className="text-white focus:bg-white/10 focus:text-white">
                Todos os gêneros
              </SelectItem>
              <SelectItem value="male" className="text-white focus:bg-white/10 focus:text-white">
                Apenas Masculino
              </SelectItem>
              <SelectItem value="female" className="text-white focus:bg-white/10 focus:text-white">
                Apenas Feminino
              </SelectItem>
              <SelectItem value="other" className="text-white focus:bg-white/10 focus:text-white">
                Apenas Outros
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-white/60">
            Filtrar quais gêneros aparecem no painel
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
